// src/app/core/service/two-factor.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@enviroment/environment';
import { AuthManagementService } from '@core/service/auth-management.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom } from 'rxjs';
import {decryptData} from "@shared/utils/decrypt";
import { DefaultResponse } from '@core/models/Http/DefaultResponse';

export interface TwoFactorMedium {
  idMedioAutenticacion: number;
  descripcion: string;
  activo: boolean;
}

@Injectable({ providedIn: 'root' })
export class TwoFactorService {
  private http = inject(HttpClient);
  private authMng = inject(AuthManagementService);
  private jwt = new JwtHelperService();
  private readonly TWO_FA_OK_KEY = 'rmd_2fa_ok_until';

  private buildAuthHeaders(asJson = false): HttpHeaders {
    const transportToken = this.authMng.getUserKey();
    const headersConfig: Record<string, string> = {
      Authorization: `Bearer ${transportToken ?? ''}`
    };

    if (asJson) {
      headersConfig['Content-Type'] = 'application/json';
    }

    return new HttpHeaders(headersConfig);
  }

  /** Envia el codigo (via SMS/Email/etc) */
  sendCode(idMedioAutenticacion?: number) {
    const headers = this.buildAuthHeaders(true);
    const body =
      typeof idMedioAutenticacion === 'number'
        ? { idMedioAutenticacion }
        : null;

    return this.http.post<DefaultResponse<boolean>>(
      `${environment.backend_url}Auth/send-2fa`,
      body,
      { headers }
    );
  }

  getMediums() {
    const headers = this.buildAuthHeaders();
    return this.http.get<DefaultResponse<TwoFactorMedium[]>>(
      `${environment.backend_url}Auth/2fa-mediums`,
      { headers }
    );
  }

  savePreferredMedium(idMedioAutenticacion: number) {
    const headers = this.buildAuthHeaders(true);
    return this.http.post<DefaultResponse<boolean>>(
      `${environment.backend_url}Auth/preferred-2fa-medium`,
      { idMedioAutenticacion },
      { headers }
    );
  }

  private set2FAOkFor(minutes: number) {
    const until = Date.now() + minutes * 60_000;
    localStorage.setItem(this.TWO_FA_OK_KEY, String(until));
  }

  private is2FAOkCached(): boolean {
    const raw = localStorage.getItem(this.TWO_FA_OK_KEY);
    const until = raw ? Number(raw) : 0;
    return !!until && !isNaN(until) && Date.now() < until;
  }

  public clear2FACache(): void {
    localStorage.removeItem(this.TWO_FA_OK_KEY);
  }

  /** Valida el codigo: body es un string plano => "123456" */
  validateCode(code: string) {
    const headers = this.buildAuthHeaders(true);

    // Importante: el codigo va como string JSON
    const body = JSON.stringify(code);

    return this.http.post(`${environment.backend_url}Auth/validate-2fa`, body, { headers });
  }


  /** Lee claims actuales del JWT (plano) */
  public getClaims(): any | null {
    const jwtPlain = this.authMng.getToken();
    if (!jwtPlain) return null;
    try {
      return this.jwt.decodeToken(jwtPlain);
    } catch {
      return null;
    }
  }

  /** True si el 2FA esta vencido (o nunca hecho) segun LastAuth2F y 60 min de vigencia */
  private is2FAStale(claims: any): boolean {
    const last = (claims?.LastAuth2F || '').trim();
    if (!last) return true; // nunca hecho
    const lastDate = new Date(last); // el back te lo manda como texto legible
    if (isNaN(lastDate.getTime())) return true;
    const diffMin = (Date.now() - lastDate.getTime()) / 60000;
    return diffMin >= 60; // vence a la hora
  }

  /** Reglas para exigir 2FA segun claims */
  mustEnforce2FA(): boolean {
    if (!environment.twoFAEnabled) return false;

    if (this.is2FAOkCached()) {
      return false;
    }

    const c = this.getClaims();

    if (!c) return false;

    const required = String(c?.Auth2FIsRequired ?? 'false').toLowerCase() === 'true';
    const isAuth  = String(c?.Auth2FIsAuth ?? 'false').toLowerCase() === 'true';

    if (!required) return false;
    if (!isAuth) return true;

    return this.is2FAStale(c);
  }

  /**
   * Flujo completo: mostrar modal -> validate -> renew -> guardar token transporte.
   * El caller decide cuando hacer el primer envio usando sendFn.
   * Devuelve true/false segun exito.
   */
  async ensure2FA(
    showPrompt: (sendFn: () => Promise<any>) => Promise<string | null>
  ): Promise<boolean> {
    try {
      if (!environment.twoFAEnabled) return true;
      if (!this.mustEnforce2FA()) return true;

      const code = await showPrompt(async () => firstValueFrom(this.sendCode()));
      if (!code) return false;

      const validateResp: any = await firstValueFrom(this.validateCode(code));

      let parsed: any = null;

      if (validateResp?.data && typeof validateResp.data === 'object') {
        parsed = validateResp.data;
      } else if (typeof validateResp?.data === 'string' && validateResp.data.includes(':')) {
        const decrypted = decryptData(validateResp.data, environment.encryptionKey);
        if (!decrypted) return false;
        parsed = JSON.parse(decrypted);
      } else {
        return false;
      }

      const authenticated =
        parsed?.authenticated2FA === true ||
        String(parsed?.authenticated2FA).toLowerCase() === 'true';

      const newTransport = parsed?.token ?? '';

      if (!authenticated || !newTransport) {
        return false;
      }

      this.authMng.saveUserKey(newTransport);
      this.set2FAOkFor(60);

      return true;
    } catch (error) {
      console.error('2FA -> error en ensure2FA', error);
      this.clear2FACache();
      return false;
    }
  }
}
